import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Smartphone, LogOut, Inbox, Clock, CheckCircle2, TrendingUp, Search,
  Phone, MessageCircle, X, Sparkles, Box, Cable, ImageOff, Eye, Users, ChevronLeft, ChevronRight,
} from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ModelsManager } from "@/components/admin/ModelsManager";
import { SettingsManager } from "@/components/admin/SettingsManager";
import { PricingManager } from "@/components/admin/PricingManager";
import { FeaturedManager } from "@/components/admin/FeaturedManager";
import { ListingsManager } from "@/components/admin/ListingsManager";

const SIDE_LABELS = {
  front: "Ön", back: "Arxa", left: "Sol", right: "Sağ", bottom: "Aşağı", top: "Yuxarı",
};
const COND_LABELS = { like_new: "Demək olar ki, yeni", good: "Yaxşı", fair: "Orta", poor: "Aşağı" };
const STATUS = {
  new: { l: "Yeni", c: "text-[#FF3B30] bg-[#FF3B30]/15 border-[#FF3B30]/40" },
  contacted: { l: "Əlaqə saxlanıb", c: "text-[#FF9F0A] bg-[#FF9F0A]/15 border-[#FF9F0A]/30" },
  no_answer: { l: "Trubkanı götürmədi", c: "text-[#FF375F] bg-[#FF375F]/15 border-[#FF375F]/30" },
  accepted: { l: "Qəbul edilib", c: "text-[#34C759] bg-[#34C759]/15 border-[#34C759]/30" },
  rejected: { l: "İmtina", c: "text-red-400 bg-red-500/15 border-red-500/30" },
};
const storageLabel = (gb) => (gb >= 1024 ? `${gb / 1024}TB` : `${gb}GB`);
const fmtDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Baku",
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(d).reduce((a, x) => { a[x.type] = x.value; return a; }, {});
  return `${p.day}.${p.month}.${p.year} ${p.hour}:${p.minute}`;
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="glass rounded-2xl p-5">
    <div className="flex items-center justify-between">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
        <div className="font-display text-2xl font-bold mt-1">{value}</div>
      </div>
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${color}`}><Icon className="w-5 h-5" /></div>
    </div>
  </div>
);

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, new: 0, accepted: 0, avg_estimate: 0, visitors_today: 0, visitors_total: 0 });
  const [subs, setSubs] = useState([]);
  const [filter, setFilter] = useState("new");
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [tab, setTab] = useState("submissions");

  const prevCountRef = useRef(null);
  const load = async (silent = false) => {
    const [s, l] = await Promise.all([api.get("/stats"), api.get("/submissions")]);
    setStats(s.data);
    if (silent && prevCountRef.current != null && l.data.length > prevCountRef.current) {
      const diff = l.data.length - prevCountRef.current;
      toast.success(diff === 1 ? "Yeni müraciət gəldi! 🔔" : `${diff} yeni müraciət gəldi! 🔔`);
    }
    prevCountRef.current = l.data.length;
    setSubs(l.data);
  };
  useEffect(() => {
    load().catch(() => toast.error("Məlumat yüklənmədi"));
    const iv = setInterval(() => load(true).catch(() => {}), 15000);
    return () => clearInterval(iv);  }, []);

  const filtered = useMemo(() => {
    return subs.filter((s) => {
      if (filter !== "all" && s.status !== filter) return false;
      if (q && !`#${s.number} ${s.number} ${s.model_name} ${s.customer_name} ${s.customer_phone}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [subs, filter, q]);

  const counts = useMemo(() => {
    const c = { all: subs.length, new: 0, contacted: 0, no_answer: 0, accepted: 0, rejected: 0 };
    subs.forEach((s) => { if (c[s.status] != null) c[s.status] += 1; });
    return c;
  }, [subs]);

  const updateStatus = async (id, status) => {
    const { data } = await api.patch(`/submissions/${id}`, { status });
    setSubs((prev) => prev.map((s) => (s.id === id ? data : s)));
    if (active?.id === id) setActive(data);
    load();
  };

  const doLogout = () => { logout(); navigate("/admin/login"); };
  const waNumber = (raw) => {
    let d = (raw || "").replace(/\D/g, "");
    if (d.startsWith("00")) d = d.slice(2);
    if (!d.startsWith("994")) {
      if (d.startsWith("0")) d = "994" + d.slice(1);
      else if (d.length === 9) d = "994" + d;
    }
    return d;
  };
  const waLink = (phone) => `https://wa.me/${waNumber(phone)}`;
  const telLink = (phone) => `tel:+${waNumber(phone)}`;
  const fmtPhone = (raw) => {
    const d = waNumber(raw);
    if (d.length === 12 && d.startsWith("994")) {
      const n = d.slice(3);
      return `+994 ${n.slice(0, 2)} ${n.slice(2, 5)} ${n.slice(5, 7)} ${n.slice(7, 9)}`;
    }
    return raw;
  };

  return (
    <div className="min-h-screen relative grain">
      <nav className="fixed top-0 inset-x-0 z-40 glass">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#2997FF] to-[#9852F6] flex items-center justify-center">
              <Smartphone className="w-5 h-5 text-white" />
            </div>
            <span className="font-display font-bold">iStore Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-400 hidden sm:inline">{user?.email}</span>
            <button onClick={doLogout} data-testid="logout-btn"
              className="flex items-center gap-1.5 text-sm text-slate-300 hover:text-white transition-colors">
              <LogOut className="w-4 h-4" /> Çıxış
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-5 pt-24 pb-16">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          <StatCard icon={Eye} label="Bugünkü ziyarətçi" value={stats.visitors_today} color="bg-[#30D158]/15 text-[#30D158]" />
          <StatCard icon={Users} label="Ümumi ziyarətçi" value={stats.visitors_total} color="bg-[#64D2FF]/15 text-[#64D2FF]" />
          <StatCard icon={Inbox} label="Ümumi müraciət" value={stats.total} color="bg-[#2997FF]/15 text-[#2997FF]" />
          <StatCard icon={Clock} label="Yeni / Gözləyən" value={stats.new} color="bg-[#FF9F0A]/15 text-[#FF9F0A]" />
          <StatCard icon={CheckCircle2} label="Qəbul edilmiş" value={stats.accepted} color="bg-[#34C759]/15 text-[#34C759]" />
          <StatCard icon={TrendingUp} label="Orta təklif" value={`${stats.avg_estimate} ₼`} color="bg-[#9852F6]/15 text-[#c9a4ff]" />
        </div>

        <div className="flex flex-wrap gap-2 mb-6 -mx-1 px-1">
          {[["submissions", "Müraciətlər"], ["models", "Modellər"], ["featured", "Populyar"], ["listings", "Satışda"], ["pricing", "Qiymətlər"], ["settings", "Ayarlar"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
              className={`shrink-0 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-sm font-medium transition-colors ${tab === k ? "bg-neutral-100 text-black" : "bg-white/5 text-neutral-400 hover:text-white"}`}>
              {l}
            </button>
          ))}
        </div>

        {tab === "submissions" && (
        <>
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="№, model, ad və ya nömrə axtar..."
              data-testid="search-input" className="bg-white/5 border-white/10 pl-10 h-11" />
          </div>
          <div className="flex gap-2 flex-wrap">
            {["new", "contacted", "no_answer", "accepted", "rejected", "all"].map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`filter-status-${f}`}
                className={`px-4 py-2 rounded-full text-xs font-medium transition-colors inline-flex items-center gap-1.5 ${
                  filter === f ? "bg-white text-black" : "bg-white/5 text-slate-400 hover:text-white"
                }`}>
                {f === "all" ? "Hamısı" : STATUS[f].l}
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold leading-none ${
                  filter === f ? "bg-black/10 text-black" : "bg-white/10 text-slate-300"
                }`}>{counts[f]}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          {filtered.length === 0 && (
            <div className="glass rounded-2xl p-12 text-center text-slate-500" data-testid="empty-state">
              Müraciət tapılmadı.
            </div>
          )}
          {filtered.map((s) => (
            <motion.div key={s.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              onClick={() => setActive(s)} data-testid={`submission-row-${s.id}`}
              className={`glass rounded-2xl p-4 flex items-center gap-4 cursor-pointer transition-colors ${s.status === "new" ? "border-2 border-[#FF3B30]/70 bg-[#FF3B30]/[0.08] ring-1 ring-[#FF3B30]/40" : "hover:border-[#2997FF]/40"}`}>
              <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center shrink-0">
                <Smartphone className="w-5 h-5 text-slate-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate"><span className="text-slate-500 font-mono">#{s.number ?? "—"}</span> {s.model_name}{s.storage ? ` · ${storageLabel(s.storage)}` : ""}</div>
                <div className="text-sm text-slate-400 truncate">{s.customer_name} · {s.customer_phone}</div>
                <div className="text-xs text-slate-500 mt-0.5">🕒 {fmtDateTime(s.created_at)}</div>
                <div className="text-xs text-[#7fbcff] mt-0.5 sm:hidden">{s.estimate ? `~${s.estimate.estimate} ₼` : "—"} · {Object.keys(s.photos || {}).length || (s.photos?.length ?? 0)} şəkil</div>
              </div>
              <div className="hidden sm:block text-right">
                <div className="font-display font-semibold text-sm">{s.estimate ? <>{`${s.estimate.low}–${s.estimate.high} ₼`} <span className="text-[#7fbcff]">· ~{s.estimate.estimate} ₼</span></> : "—"}</div>
                <div className="text-xs text-slate-500">{Object.keys(s.photos || {}).length || (s.photos?.length ?? 0)} şəkil</div>
              </div>
              <span className={`text-xs px-2.5 sm:px-3 py-1 rounded-full border shrink-0 ${STATUS[s.status]?.c}`}>{STATUS[s.status]?.l}</span>
            </motion.div>
          ))}
        </div>
        </>
        )}

        {tab === "models" && <ModelsManager onChanged={load} />}
        {tab === "featured" && <FeaturedManager />}
        {tab === "listings" && <ListingsManager />}
        {tab === "pricing" && <PricingManager />}
        {tab === "settings" && <SettingsManager />}
      </div>

      {/* DETAIL MODAL */}
      {active && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-5" data-testid="submission-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setActive(null)} />
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}
            className="relative glass rounded-t-3xl sm:rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
            <div className="sticky top-0 glass px-6 py-4 flex items-center justify-between border-b border-white/8 z-10">
              <div>
                <h3 className="font-display text-lg font-bold"><span className="text-slate-500 font-mono">#{active.number ?? "—"}</span> {active.model_name}</h3>
                <p className="text-xs text-slate-400">{[
                  active.storage ? storageLabel(active.storage) : null,
                  active.color || null,
                  active.sim_type === "esim" ? "Yalnız eSIM" : active.sim_type === "dualsim" ? "Dual SIM + eSIM" : active.sim_type === "dual" ? "Fiziki SIM + eSIM" : null,
                  COND_LABELS[active.condition],
                ].filter(Boolean).join(" · ")}</p>
              </div>
              <button onClick={() => setActive(null)} data-testid="modal-close" className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {active.estimate && (
                <div className="flex items-center gap-2 px-4 py-3 rounded-2xl bg-[#2997FF]/10 border border-[#2997FF]/30 text-[#7fbcff]">
                  <Sparkles className="w-4 h-4" /> Təxmini dəyər: <b>{active.estimate.low}–{active.estimate.high} AZN</b> · Yekun: <b>~{active.estimate.estimate} AZN</b>
                </div>
              )}

              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Şəkillər</div>
                {(active.photos?.length ?? 0) === 0 ? (
                  <div className="flex items-center gap-2 text-slate-500 text-sm"><ImageOff className="w-4 h-4" /> Şəkil yüklənməyib</div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {active.photos.map((p, idx) => (
                      <button key={p.side} type="button" onClick={() => setLightbox(idx)} data-testid={`photo-thumb-${p.side}`}
                        className="relative rounded-xl overflow-hidden aspect-square group">
                        <img src={fileUrl(p.url)} alt={p.side} className="w-full h-full object-cover" />
                        <span className="absolute bottom-1 left-1 text-[10px] px-1.5 py-0.5 rounded bg-black/60">{SIDE_LABELS[p.side] || p.side}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm">
                {active.battery_health ? (
                  <div className="flex items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2 col-span-2" data-testid="detail-battery-health">
                    <span className="text-slate-400">Batareya sağlamlığı</span>
                    <span className={Number(active.battery_health) >= 90 ? "text-[#34C759]" : "text-[#FF9F0A]"}>{active.battery_health}%</span>
                  </div>
                ) : null}
                {[
                  ["Ekran dəyişib", active.screen_replaced],
                  ["Batareya dəyişib", active.battery_replaced],
                  ["Arxa qapaq dəyişib", active.back_replaced],
                  ["Təmir / yıxılıb", active.repaired_dropped],
                  ["Cızıqlar", active.scuffs],
                  ["Çatlar / çip", active.chips],
                  ["Ekran cızıqları", active.screen_scratches],
                ].map(([l, v]) => (
                  <div key={l} className="flex items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2">
                    <span className="text-slate-400">{l}</span>
                    <span className={v ? "text-[#FF9F0A]" : "text-[#34C759]"}>{v ? "Bəli" : "Xeyr"}</span>
                  </div>
                ))}
                {active.faceid_working != null && (
                  <div className="flex items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2">
                    <span className="text-slate-400">Face ID işləyir</span>
                    <span className={active.faceid_working ? "text-[#34C759]" : "text-[#FF9F0A]"}>{active.faceid_working ? "Bəli" : "Xeyr"}</span>
                  </div>
                )}
                {active.registered != null && (
                  <div className="flex items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2">
                    <span className="text-slate-400">Şəbəkə qeydiyyatı</span>
                    <span className={active.registered ? "text-[#34C759]" : "text-[#FF9F0A]"}>{active.registered ? "Var" : "Yox"}</span>
                  </div>
                )}
                {active.beyanname != null && (
                  <div className="flex items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2">
                    <span className="text-slate-400">Bəyannamə</span>
                    <span className={active.beyanname ? "text-[#34C759]" : "text-[#FF9F0A]"}>{active.beyanname ? "Var" : "Yox"}</span>
                  </div>
                )}
                {active.wifi_working != null && (
                  <div className="flex items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2">
                    <span className="text-slate-400">Wi‑Fi</span>
                    <span className={active.wifi_working ? "text-[#34C759]" : "text-[#FF9F0A]"}>{active.wifi_working ? "İşləyir" : "İşləmir"}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 rounded-lg bg-white/[0.03] px-3 py-2"><Box className="w-4 h-4 text-slate-400" /> Qutu: <span className={active.has_box ? "text-[#34C759]" : "text-slate-500"}>{active.has_box ? "Var" : "Yox"}</span></div>
                <div className="flex items-center gap-2 rounded-lg bg-white/[0.03] px-3 py-2"><Cable className="w-4 h-4 text-slate-400" /> Kabel: <span className={active.has_cable ? "text-[#34C759]" : "text-slate-500"}>{active.has_cable ? "Var" : "Yox"}</span></div>
              </div>

              {active.note && <div className="rounded-xl bg-white/[0.03] p-3 text-sm text-slate-300">{active.note}</div>}

              <div className="rounded-xl bg-white/[0.03] p-4">
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Müştəri</div>
                <div className="font-medium">{active.customer_name}</div>
                <div className="text-sm text-slate-400 flex items-center gap-2 mt-1"><Phone className="w-4 h-4" /> {fmtPhone(active.customer_phone)}</div>
                <div className="flex gap-2 mt-3">
                  <a href={waLink(active.customer_phone)} target="_blank" rel="noreferrer" data-testid="whatsapp-btn"
                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#25D366]/20 text-[#4ade80] border border-[#25D366]/30 text-sm hover:bg-[#25D366]/30 transition-colors">
                    <MessageCircle className="w-4 h-4" /> WhatsApp
                  </a>
                  <a href={telLink(active.customer_phone)} className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-sm hover:bg-white/5 transition-colors">
                    <Phone className="w-4 h-4" /> Zəng
                  </a>
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Status dəyiş</div>
                <div className="flex gap-2 flex-wrap">
                  {Object.entries(STATUS).map(([k, v]) => (
                    <button key={k} onClick={() => updateStatus(active.id, k)} data-testid={`set-status-${k}`}
                      className={`px-4 py-2 rounded-full text-xs font-medium border transition-colors ${active.status === k ? v.c : "border-white/10 text-slate-400 hover:text-white"}`}>
                      {v.l}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {active && lightbox !== null && active.photos?.[lightbox] && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90" data-testid="photo-lightbox" onClick={() => setLightbox(null)}>
          <button onClick={() => setLightbox(null)} data-testid="lightbox-close"
            className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
            <X className="w-5 h-5" />
          </button>
          {active.photos.length > 1 && (
            <>
              <button data-testid="lightbox-prev" onClick={(e) => { e.stopPropagation(); setLightbox((lightbox - 1 + active.photos.length) % active.photos.length); }}
                className="absolute left-3 w-11 h-11 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button data-testid="lightbox-next" onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % active.photos.length); }}
                className="absolute right-3 w-11 h-11 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
          <img src={fileUrl(active.photos[lightbox].url)} alt="" onClick={(e) => e.stopPropagation()}
            className="max-w-[92vw] max-h-[86vh] object-contain rounded-lg" />
          <span className="absolute bottom-5 left-1/2 -translate-x-1/2 text-sm px-3 py-1 rounded-full bg-black/70">
            {SIDE_LABELS[active.photos[lightbox].side] || active.photos[lightbox].side} · {lightbox + 1}/{active.photos.length}
          </span>
        </div>
      )}
    </div>
  );
}
